/**
 * Legacy Maximizer reconciliation dialog.
 *
 * Workflow:
 *   1. User uploads up to 4 .xlsx files (or one .zip containing them).
 *   2. Backend returns a session_id + diff list. Nothing is written.
 *   3. User reviews each row, ticks the diffs they want to apply.
 *   4. Click "Apply selected (X)" → triggers a mandatory backup, then
 *      writes only the ticked diffs to sales_data in one batch.
 *
 * UX guarantees (per spec):
 *   - No DB write until the user explicitly clicks Apply.
 *   - Pre-commit dialog explains: "X changes across Y orgs; backup created
 *     first; can rollback via PITR using <backup_filename>".
 *   - Skipped/unselected items remain visible across reloads via the
 *     /sessions list endpoint.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Upload, AlertTriangle, FileSpreadsheet, ShieldCheck, History } from 'lucide-react';
import { authFetch } from '../lib/api';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const SHEET_LABEL = {
  accounts: 'Accounts',
  photography: 'Photography',
  production: 'Production',
  sales: 'Sales',
  unknown: 'Unknown',
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return <span className="text-muted-foreground italic">empty</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

export const LegacyReconcileDialog = ({ open, onOpenChange }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [session, setSession] = useState(null);  // { session_id, diffs:[], sheets:[], … }
  const [committing, setCommitting] = useState(false);
  const [filterSheet, setFilterSheet] = useState('all');
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');  // all | pending | apply | skip
  const [previousSessions, setPreviousSessions] = useState([]);

  const reset = () => {
    setFiles([]);
    setSession(null);
    setFilterSheet('all');
    setFilterText('');
    setFilterStatus('all');
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  // List previous sessions when the dialog mounts so user can re-open old
  // ones (skipped items survive between visits per spec).
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await authFetch(`${API}/reconcile/sessions`);
        if (r.ok) {
          const j = await r.json();
          setPreviousSessions(j.sessions || []);
        }
      } catch (_e) { /* noop */ }
    })();
  }, [open]);

  const handleUpload = async () => {
    if (!files.length) {
      toast.error('Pick at least one .xlsx or .zip file first');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const r = await authFetch(`${API}/reconcile/upload`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.text()).slice(0, 300));
      const j = await r.json();
      // Pull full session (with diffs)
      const r2 = await authFetch(`${API}/reconcile/${j.session_id}`);
      if (!r2.ok) throw new Error('Session fetch failed');
      const full = await r2.json();
      setSession(full);
      toast.success(`Loaded — ${j.diff_count} differences across ${j.field_diff_count} field changes`);
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const loadExistingSession = async (sid) => {
    try {
      const r = await authFetch(`${API}/reconcile/${sid}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSession(await r.json());
    } catch (e) {
      toast.error(`Could not load session: ${e.message}`);
    }
  };

  const visibleDiffs = useMemo(() => {
    if (!session) return [];
    return session.diffs.filter((d) => {
      if (filterSheet !== 'all' && d.sheet !== filterSheet) return false;
      if (filterStatus !== 'all' && d.status !== filterStatus) return false;
      if (filterText.trim()) {
        const blob = `${d.org_name || ''} ${d.legacy_company || ''} ${d.field || ''} ${d.legacy_value || ''} ${d.current_value || ''}`.toLowerCase();
        if (!blob.includes(filterText.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [session, filterSheet, filterStatus, filterText]);

  const counts = useMemo(() => {
    if (!session) return { total: 0, apply: 0, skip: 0, pending: 0, missing: 0 };
    let apply = 0, skip = 0, pending = 0, missing = 0;
    session.diffs.forEach((d) => {
      if (d.kind === 'missing_org') { missing += 1; return; }
      if (d.status === 'apply') apply += 1;
      else if (d.status === 'skip') skip += 1;
      else pending += 1;
    });
    return { total: session.diffs.length, apply, skip, pending, missing };
  }, [session]);

  const patchOne = async (diffId, status) => {
    try {
      const r = await authFetch(`${API}/reconcile/${session.session_id}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: [{ diff_id: diffId, status }] }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Optimistic update
      setSession((s) => ({
        ...s,
        diffs: s.diffs.map((d) => d.diff_id === diffId ? { ...d, status } : d),
      }));
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    }
  };

  const patchVisible = async (status) => {
    if (!visibleDiffs.length) return;
    const ids = visibleDiffs.filter((d) => d.kind === 'field_diff').map((d) => d.diff_id);
    if (!ids.length) return;
    try {
      const r = await authFetch(`${API}/reconcile/${session.session_id}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: ids.map((id) => ({ diff_id: id, status })) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSession((s) => ({
        ...s,
        diffs: s.diffs.map((d) => ids.includes(d.diff_id) ? { ...d, status } : d),
      }));
      toast.success(`Marked ${ids.length} as "${status}"`);
    } catch (e) {
      toast.error(`Bulk update failed: ${e.message}`);
    }
  };

  const handleCommit = async () => {
    if (!counts.apply) {
      toast.error('Tick at least one row to apply');
      return;
    }
    const orgsTouched = new Set(session.diffs.filter((d) => d.status === 'apply' && d.kind === 'field_diff').map((d) => d.org_id)).size;
    if (!window.confirm(
      `Apply ${counts.apply} change${counts.apply === 1 ? '' : 's'} across ${orgsTouched} organisation${orgsTouched === 1 ? '' : 's'}.\n\n` +
      `A full database backup will be created automatically BEFORE any changes are made — you can roll back via Settings → Point-in-Time Recovery if anything looks off.\n\n` +
      `Continue?`,
    )) return;
    setCommitting(true);
    try {
      const r = await authFetch(`${API}/reconcile/${session.session_id}/commit`, { method: 'POST' });
      if (!r.ok) throw new Error((await r.text()).slice(0, 300));
      const j = await r.json();
      toast.success(
        `Applied ${j.applied} change${j.applied === 1 ? '' : 's'} across ${j.orgs_updated} org${j.orgs_updated === 1 ? '' : 's'}. ` +
        `Backup: ${j.backup_filename || '(none)'}.`,
        { duration: 10000 },
      );
      setSession(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Commit failed: ${e.message}`);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col gap-3" data-testid="legacy-reconcile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Legacy Maximizer Reconcile
          </DialogTitle>
          <DialogDescription>
            Upload an extract from the legacy Maximizer database and review every difference against G.S Workflow. Nothing is written until you tick the rows you want and click <strong>Apply selected</strong>. A backup is created first — you can roll back via Point-in-Time Recovery.
          </DialogDescription>
        </DialogHeader>

        {/* ---------------- Upload step ---------------- */}
        {!session && (
          <div className="space-y-4 overflow-auto min-h-0 flex-1">
            <div className="rounded-lg border border-dashed p-6 flex flex-col items-center gap-3">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground text-center">
                Drop the four .xlsx exports here, or a single .zip bundle.
              </p>
              <Input
                type="file"
                multiple
                accept=".xlsx,.zip"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="max-w-md"
                data-testid="reconcile-file-input"
              />
              {files.length > 0 && (
                <div className="text-xs text-muted-foreground text-center">
                  {files.length} file{files.length === 1 ? '' : 's'} ready: {files.map((f) => f.name).join(', ')}
                </div>
              )}
              <Button
                onClick={handleUpload}
                disabled={!files.length || uploading}
                data-testid="reconcile-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Analyse extract
              </Button>
            </div>

            {previousSessions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                  <History className="w-4 h-4" />
                  Previous reconciliation sessions
                </h4>
                <div className="space-y-1.5 max-h-48 overflow-auto pr-1">
                  {previousSessions.slice(0, 12).map((s) => (
                    <button
                      key={s.session_id}
                      onClick={() => loadExistingSession(s.session_id)}
                      className="w-full text-left p-2 rounded-md border hover:bg-muted/50 transition-colors text-sm flex justify-between items-center"
                      data-testid={`reconcile-session-${s.session_id}`}
                    >
                      <span>
                        <span className="font-mono text-xs">{s.session_id}</span>
                        {' · '}
                        {new Date(s.created_at).toLocaleString()}
                      </span>
                      <span className="flex gap-1.5">
                        {s.committed && <Badge variant="default">committed</Badge>}
                        {!s.committed && <Badge variant="outline">draft</Badge>}
                        {s.applied_count > 0 && <Badge variant="secondary">{s.applied_count} applied</Badge>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------------- Diff review step ---------------- */}
        {session && (
          <>
            <div className="flex flex-wrap gap-2 items-center text-sm">
              <Badge variant="outline" data-testid="counts-total">{counts.total} total</Badge>
              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" data-testid="counts-apply">{counts.apply} to apply</Badge>
              <Badge variant="secondary" data-testid="counts-pending">{counts.pending} pending</Badge>
              <Badge variant="secondary" data-testid="counts-skip">{counts.skip} skip</Badge>
              {counts.missing > 0 && (
                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" data-testid="counts-missing">
                  {counts.missing} unmatched org{counts.missing === 1 ? '' : 's'}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground ml-2">Session {session.session_id}</span>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={filterSheet}
                onChange={(e) => setFilterSheet(e.target.value)}
                className="px-2 py-1 text-sm border rounded-md bg-background"
                data-testid="reconcile-filter-sheet"
              >
                <option value="all">All sheets</option>
                {Object.keys(SHEET_LABEL).map((k) => (
                  <option key={k} value={k}>{SHEET_LABEL[k]}</option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-2 py-1 text-sm border rounded-md bg-background"
                data-testid="reconcile-filter-status"
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="apply">To apply</option>
                <option value="skip">Skip</option>
              </select>
              <Input
                placeholder="Filter by org / field / value…"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="max-w-sm h-8 text-sm"
                data-testid="reconcile-filter-text"
              />
              <div className="flex-1" />
              <Button size="sm" variant="outline" onClick={() => patchVisible('apply')} data-testid="bulk-apply-btn">
                Tick all visible
              </Button>
              <Button size="sm" variant="outline" onClick={() => patchVisible('skip')} data-testid="bulk-skip-btn">
                Skip all visible
              </Button>
            </div>

            <div className="flex-1 min-h-0 border rounded-md overflow-auto" data-testid="reconcile-diff-scroll">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left w-10"></th>
                    <th className="px-2 py-2 text-left">Sheet</th>
                    <th className="px-2 py-2 text-left">Organisation</th>
                    <th className="px-2 py-2 text-left">Field</th>
                    <th className="px-2 py-2 text-left">Current value</th>
                    <th className="px-2 py-2 text-left">Legacy value</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDiffs.slice(0, 1000).map((d) => {
                    if (d.kind === 'missing_org') {
                      return (
                        <tr key={d.diff_id} className="border-t bg-amber-500/5">
                          <td className="px-2 py-1.5">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                          </td>
                          <td className="px-2 py-1.5">{SHEET_LABEL[d.sheet] || d.sheet}</td>
                          <td className="px-2 py-1.5 italic text-muted-foreground" colSpan={4}>
                            Unmatched legacy company: <strong>{d.legacy_company}</strong> ({d.legacy_department || '—'})
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={d.diff_id} className={`border-t ${d.status === 'apply' ? 'bg-emerald-500/5' : d.status === 'skip' ? 'opacity-60' : ''}`}>
                        <td className="px-2 py-1.5">
                          <Checkbox
                            checked={d.status === 'apply'}
                            onCheckedChange={(v) => patchOne(d.diff_id, v ? 'apply' : 'pending')}
                            data-testid={`diff-check-${d.diff_id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-xs">{SHEET_LABEL[d.sheet] || d.sheet}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-medium">{d.org_name}</div>
                          <div className="text-xs text-muted-foreground">{d.legacy_department}</div>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-xs">{d.field}<div className="text-[11px] text-muted-foreground">({d.header})</div></td>
                        <td className="px-2 py-1.5 max-w-xs break-words text-xs">{fmtVal(d.current_value)}</td>
                        <td className="px-2 py-1.5 max-w-xs break-words text-xs">
                          <strong className="text-emerald-700 dark:text-emerald-400">{fmtVal(d.legacy_value)}</strong>
                          <button
                            onClick={() => patchOne(d.diff_id, 'skip')}
                            className="ml-2 text-[11px] text-muted-foreground hover:text-rose-600 underline"
                            data-testid={`skip-${d.diff_id}`}
                          >skip</button>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleDiffs.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No diffs match the current filters.</td></tr>
                  )}
                  {visibleDiffs.length > 1000 && (
                    <tr><td colSpan={6} className="px-3 py-2 text-center text-xs text-muted-foreground">Showing the first 1,000 — narrow the filters to see the rest.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 mt-2">
          {session && (
            <Button
              onClick={handleCommit}
              disabled={committing || counts.apply === 0}
              data-testid="reconcile-commit-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {committing
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <ShieldCheck className="w-4 h-4 mr-1" />}
              Apply selected ({counts.apply})
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={committing}>
            {session ? 'Close (keep session)' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LegacyReconcileDialog;
